import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Card, Alert, Spinner, Image, Dropdown, Row, Col } from 'react-bootstrap';
import { useNavigate, Navigate } from 'react-router-dom';
import StoryService from '../services/story';
import { useTheme } from '../context/ThemeContext';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import './StoryDetail.css'; // Reuse the same CSS for drag and drop
import './CreateStory.css'; // New CSS for modern design

const CreateStory = () => {
  const { darkMode } = useTheme();
  const { isAuthenticated } = useAuth();
  const { apps, currentApp, switchApp, loading: appsLoading } = useApp();
  const [text, setText] = useState('');
  const [circularImage, setCircularImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [textUpdated, setTextUpdated] = useState(false);
  const textUpdateTimer = useRef(null);
  
  // Placeholder image for circular template
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23f0f0f0'/%3E%3Cpath d='M35 30h30v40H35z' fill='%23d0d0d0'/%3E%3Cpath d='M35 50l10-10 5 5 15-15 5 5v35H35z' fill='%23a0a0a0'/%3E%3Ccircle cx='45' cy='40' r='5' fill='%23808080'/%3E%3C/svg%3E";
  const navigate = useNavigate();
  
  useEffect(() => {
    // Set the selected app to the current app from context when component mounts
    if (currentApp) {
      setSelectedApp(currentApp);
    }
  }, [currentApp]);
  
  // Handle text change with animation effect
  const handleTextChange = (e) => {
    setText(e.target.value);
    setTextUpdated(true);
    
    // Clear previous timer if it exists
    if (textUpdateTimer.current) {
      clearTimeout(textUpdateTimer.current);
    }
    
    // Set a timer to remove the animation class after 500ms
    textUpdateTimer.current = setTimeout(() => {
      setTextUpdated(false);
    }, 500);
  };

  const handleImageChange = (e) => {
    let file;
    
    // Handle both drag-drop and file input
    if (e.dataTransfer) {
      file = e.dataTransfer.files[0];
    } else if (e.target.files) {
      file = e.target.files[0];
    }
    
    if (file) {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        setError('Please select an image file');
        return;
      }
      
      // Check file size (max 50MB)
      if (file.size > 20 * 1024 * 1024) {
        setError('Image size should be less than 20MB');
        return;
      }
      
      setCircularImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };
  
  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleImageChange(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!circularImage) {
      console.log('No circular image found in state');
      setError('Please select a circular image for your story');
      return;
    }
    
    console.log('Image selected:', circularImage.name);
    
    try {
      setLoading(true);
      
      if (!selectedApp) {
        setError('Please select an app for this story');
        setLoading(false);
        return;
      }
      
      const response = await StoryService.createStory(text, circularImage, selectedApp.id);
      
      if (response && response.storyId) {
        navigate(`/story/${response.storyId}`);
      } else {
        setError('Failed to create story. Please try again.');
      }
    } catch (err) {
      setError('Failed to create story: ' + (err.response?.data));
    } finally {
      setLoading(false);
    }
  };

  const handleAppChange = (app) => {
    setSelectedApp(app);
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Container className="create-story-container">
      <Card>
        <Card.Header as="h4">Create New Story</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          {!appsLoading && apps.length === 0 && (
            <Alert variant="warning">
              You need to create an app before you can create stories.
              <Button
                variant="link"
                onClick={() => navigate('/apps')}
                className="p-0 ms-2"
              >
                Go to Apps
              </Button>
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Select App</Form.Label>
              <Dropdown>
                <Dropdown.Toggle
                  variant="primary"
                  id="app-dropdown"
                  disabled={appsLoading || apps.length === 0}
                  className="w-100 text-start"
                >
                  {selectedApp ? selectedApp.name : 'Select an app'}
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {apps.map(app => (
                    <Dropdown.Item
                      key={app.id}
                      onClick={() => handleAppChange(app)}
                      active={selectedApp && selectedApp.id === app.id}
                    >
                      {app.name}
                    </Dropdown.Item>
                  ))}
                  <Dropdown.Divider />
                  <Dropdown.Item as="button" onClick={() => navigate('/apps')}>
                    Manage Apps
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            <div style={{ flex: '0.5', display: 'flex', flexDirection: 'column' }}>
            <Form.Group>
              <Form.Label>Circular Image</Form.Label>
              <Form.Text className="text-muted d-block mb-2">
                The image must be square and not exceed 1024x1024 resolution.
              </Form.Text>
              <div
                className={`file-drop-area ${isDragging ? 'dragging' : ''} ${circularImage ? 'file-selected' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="file-drop-icon">
                  <i className="bi bi-plus-circle"></i>
                </div>
                <div className="file-drop-message">
                  Drag & drop your image here
                </div>
                <div className="file-input-container">
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="file-input"
                    id="circularImageInput"
                    // Remove required attribute as we're handling validation in JS
                  />
                  <Button
                    variant={circularImage ? "success" : "primary"}
                    size="sm"
                    className="browse-button"
                  >
                    {circularImage ? "File Selected ✓" : "Select File"}
                  </Button>
                </div>
                <div className="file-format-hint">
                  Supported formats: JPG, PNG, GIF (max 5MB)
                </div>
                {circularImage && (
                  <div className="text-success mt-2 small">
                    <i className="bi bi-check-circle-fill me-1"></i>
                    File "{circularImage.name}" selected
                  </div>
                )}
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3 mt-3">
              <Form.Label>Story Text (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={text}
                onChange={handleTextChange}
                placeholder="Enter a text"
                style={{ textAlign: 'center' }}
              />
            </Form.Group>
            </div>

            {/* Story Preview Section - Next to the upload section */}
            <div className="story-preview-section mb-4" style={{ flex: '1' }}>
              <div className="preview-decoration preview-decoration-1"></div>
              <div className="preview-decoration preview-decoration-2"></div>
              
              <div className="story-preview-title">
                <i className="bi bi-eye-fill"></i> Story Preview
              </div>
              
              <div className="preview-container">
                <div className="preview-left">
                  <div className="story-preview-content">
                    <div className="story-circular-image">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Story thumbnail" />
                      ) : (
                        <img src={placeholderImage} alt="Story thumbnail placeholder" />
                      )}
                    </div>
                    
                    <div className={`story-text-container ${!text ? 'empty' : ''} ${textUpdated ? 'text-updated' : ''}`}>
                      {text || "Your story text will appear here..."}
                    </div>
                  </div>
                </div>
                
                <div className="preview-right d-none d-lg-block">
                  <div className="mobile-device-preview">
                    <div className="mobile-device-screen">
                      <div className="mobile-device-status-bar"></div>
                      <div className="mobile-device-content">
                        <div className="mobile-circular-image">
                          {imagePreview ? (
                            <img src={imagePreview} alt="Story thumbnail" />
                          ) : (
                            <img src={placeholderImage} alt="Story thumbnail placeholder" />
                          )}
                        </div>
                        <div className={`mobile-story-text ${!text ? 'empty' : ''} ${textUpdated ? 'text-updated' : ''}`}>
                          {text || "Your story text will appear here..."}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            </div>

            <div className="d-flex justify-content-between">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/stories')}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading || !circularImage || !selectedApp}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Creating...
                  </>
                ) : (
                  'Create New Story'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default CreateStory;